import { useState } from "react";
import { Button } from "@/components/ui/button";
import { track } from "@/lib/track-lazy";
import { ArrowRight, Check, Zap } from "lucide-react";

const ANNUAL_DISCOUNT = 0.15;

// Business is the only tier with a true monthly/annual toggle on the page.
// Team is monthly-only (low-friction signup), Enterprise is always annual
// (procurement reality). Encoded here so the JSX stays declarative.
const BUSINESS_MONTHLY = 1490;
const BUSINESS_ANNUAL = Math.round(BUSINESS_MONTHLY * (1 - ANNUAL_DISCOUNT));
const BUSINESS_ANNUAL_YEAR = BUSINESS_ANNUAL * 12;
const BUSINESS_ANNUAL_SAVINGS = (BUSINESS_MONTHLY - BUSINESS_ANNUAL) * 12;

const fmtUSD = (n: number) =>
  `$${n.toLocaleString("en-US")}`;

type Tier = {
  name: string;
  price: string;
  listPrice?: string;
  period: string;
  description: string;
  features: string[];
  cta: string;
  href: string;
  highlight?: boolean;
  badge?: string;
  note?: string;
};

const tiers: Tier[] = [
  {
    name: "Design Partner",
    price: "$0",
    period: "for 4 weeks",
    description:
      "First 3-5 pilots. Free, time-bounded. Post-pilot price named upfront in the agreement.",
    features: [
      "Full product access during pilot",
      "Direct founder support",
      "4-week scope, success metric defined upfront",
      "1-page pilot report delivered at end",
    ],
    cta: "Apply for pilot",
    href: "#waitlist",
    note: "Limited to 5 active pilots at a time",
  },
  {
    name: "Team",
    price: "$390",
    listPrice: "$500",
    period: "/mo",
    description:
      "YC, Seed, or Series A teams. Up to 25 engineers, 25 repos, 5 Confluence spaces.",
    features: [
      "Freshness detection on every PR",
      "AI drafter (100 drafts + 500 summaries / month)",
      "Owner / Approver / Viewer roles",
      "Multi-space Confluence (up to 5)",
      "Audit log + repo diagnostics",
      "Card billing, 30-day cancel",
    ],
    cta: "Start with free pilot",
    href: "#waitlist",
    highlight: true,
    badge: "Most teams start here",
    note: "YC alumni: 50% off first 12 months",
  },
  {
    name: "Business",
    price: "$1,490",
    period: "/mo",
    description:
      "Series B+. 25-100 engineers, 25-100 repos, multi-team review.",
    features: [
      "Everything in Team",
      "Champion-per-service approval gating",
      "Route-file detection (no OpenAPI required)",
      "400 drafts + 2,000 summaries / month",
      "Up to 100 repos, 25 seats",
      "ACH billing, quarterly or annual (15% off annual)",
    ],
    cta: "Start with free pilot",
    href: "#waitlist",
  },
  {
    name: "Enterprise",
    price: "$4,500+",
    period: "/mo or $2,900 BYOK",
    description:
      "100+ engineers, compliance, SOC 2 / SSO required.",
    features: [
      "Everything in Business",
      "SSO / SAML + SCIM",
      "Audit export (CSV / JSON)",
      "Custom AI quota or BYOK (Bring Your Own Key)",
      "MSA, DPA, SOC 2 on standard packet",
      "Dedicated AM, SLA, annual PO",
    ],
    cta: "Talk to us",
    href: "#waitlist",
  },
];

const Pricing = () => {
  const [billingPeriod, setBillingPeriod] = useState<"monthly" | "annual">(
    "monthly",
  );

  const handleBillingToggle = (next: "monthly" | "annual") => {
    if (next === billingPeriod) return;
    setBillingPeriod(next);
    track("Pricing Billing Toggled", { period: next });
  };

  return (
    <section className="section-padding" id="pricing">
      <div className="container-wide">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <p className="text-xs font-medium uppercase tracking-widest text-accent mb-3">
            Pricing
          </p>
          <h2 className="heading-lg mb-4">Honest pricing. Named upfront in pilot.</h2>
          <p className="body-md text-muted-foreground">
            The 4-week pilot is free. After pilot you choose Team or Business based on team size.
            Pricing scales with engineers and repos — not seats. You're not penalized for adding readers.
          </p>
        </div>

        {/* Monthly/Annual billing toggle. Affects Business price only —
            Team is monthly-only, Enterprise is annual-only by procurement reality. */}
        <div className="flex flex-col items-center gap-2 mb-10">
          <div className="inline-flex bg-surface-subtle/60 rounded-lg p-1 border border-border/40">
            <button
              type="button"
              onClick={() => handleBillingToggle("monthly")}
              className={`px-5 py-2 text-xs font-semibold rounded-md transition-colors ${
                billingPeriod === "monthly"
                  ? "bg-accent text-accent-foreground"
                  : "text-muted-foreground hover:text-foreground"
              }`}
              aria-pressed={billingPeriod === "monthly"}
            >
              Monthly
            </button>
            <button
              type="button"
              onClick={() => handleBillingToggle("annual")}
              className={`px-5 py-2 text-xs font-semibold rounded-md transition-colors ${
                billingPeriod === "annual"
                  ? "bg-accent text-accent-foreground"
                  : "text-muted-foreground hover:text-foreground"
              }`}
              aria-pressed={billingPeriod === "annual"}
            >
              Annual <span className="opacity-70 ml-1">−15%</span>
            </button>
          </div>
          <p className="text-[10px] text-muted-foreground/70">
            Annual discount applies to Business. Team is monthly-only. Enterprise is always annual.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-4 gap-6 mb-16">
          {tiers.map((tier) => {
            const isAnnualBusiness =
              billingPeriod === "annual" && tier.name === "Business";
            const displayPrice = isAnnualBusiness
              ? fmtUSD(BUSINESS_ANNUAL)
              : tier.price;
            const displayListPrice = isAnnualBusiness
              ? fmtUSD(BUSINESS_MONTHLY)
              : tier.listPrice;
            const displayPeriod = isAnnualBusiness ? "/mo · billed annually" : tier.period;
            const isEnterprise = tier.name === "Enterprise";
            return (
          <div
            key={tier.name}
            className={`surface-card p-6 flex flex-col relative ${
              tier.highlight ? "neon-border-accent neon-glow-accent" : ""
            }`}
          >
            {tier.badge && (
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1 px-3 py-1 rounded-full bg-accent text-accent-foreground text-[10px] font-semibold uppercase tracking-widest whitespace-nowrap">
                <Zap className="h-3 w-3" />
                {tier.badge}
              </div>
            )}
            <h3
              className="text-lg font-bold mb-1"
              style={{ fontFamily: "'Clash Display', 'Lexend', system-ui, sans-serif" }}
            >
              {tier.name}
            </h3>
            <p className="text-xs text-muted-foreground mb-4 leading-relaxed min-h-[4.5rem]">
              {tier.description}
            </p>
            <div className="mb-2 flex items-baseline flex-wrap gap-x-2">
              <span className="text-3xl font-bold">{displayPrice}</span>
              {displayListPrice && (
                <span className="text-sm text-muted-foreground/60 line-through">
                  {displayListPrice}
                </span>
              )}
              <span className="text-xs text-muted-foreground">{displayPeriod}</span>
            </div>
            {isAnnualBusiness && (
              <p className="text-[10px] text-accent font-semibold mb-3">
                {fmtUSD(BUSINESS_ANNUAL_YEAR)}/yr · save {fmtUSD(BUSINESS_ANNUAL_SAVINGS)}/year
              </p>
            )}
            {isEnterprise && (
              <p className="text-[10px] text-muted-foreground/70 italic mb-3">
                Annual contract required
              </p>
            )}
            {!isAnnualBusiness && !isEnterprise && <div className="mb-3" />}
            <ul className="space-y-2 mb-6 flex-1">
              {tier.features.map((f) => (
                <li
                  key={f}
                  className="flex items-start gap-2 text-xs text-muted-foreground leading-relaxed"
                >
                  <Check className="h-3.5 w-3.5 text-accent shrink-0 mt-0.5" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
            {tier.note && (
              <p className="text-[10px] text-muted-foreground/70 italic mb-3 text-center">
                {tier.note}
              </p>
            )}
            <Button variant={tier.highlight ? "accent" : "outline"} className="w-full" asChild>
              <a
                href={tier.href}
                onClick={() =>
                  track("Pricing CTA Clicked", { tier: tier.name, cta: tier.cta })
                }
              >
                {tier.cta}
                <ArrowRight className="ml-1 h-3.5 w-3.5" />
              </a>
            </Button>
          </div>
            );
          })}
        </div>

        {/* Schema.org Product + AggregateOffer for AI/SERP citations */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Product",
            name: "AntAtlas",
            description:
              "Documentation freshness platform. Watches GitHub + Confluence read-only, flags pages that disagree with code, proposes approval-gated updates.",
            brand: { "@type": "Brand", name: "AntAtlas" },
            url: "https://antatlas.ai/pricing",
            offers: {
              "@type": "AggregateOffer",
              priceCurrency: "USD",
              lowPrice: "0",
              highPrice: "15000",
              offerCount: 4,
              offers: [
                {
                  "@type": "Offer",
                  name: "Design Partner",
                  price: "0",
                  priceCurrency: "USD",
                  availability: "https://schema.org/InStock",
                  description:
                    "Free 4-week pilot. First 3-5 customers. Post-pilot price named upfront.",
                  url: "https://antatlas.ai/pricing",
                },
                {
                  "@type": "Offer",
                  name: "Team",
                  price: "390",
                  priceCurrency: "USD",
                  availability: "https://schema.org/InStock",
                  description:
                    "YC, Seed, or Series A teams. Up to 25 engineers, 25 repos, 5 Confluence spaces. Includes AI drafter (100 drafts + 500 summaries per month).",
                  priceSpecification: {
                    "@type": "UnitPriceSpecification",
                    price: "390",
                    priceCurrency: "USD",
                    referenceQuantity: { "@type": "QuantitativeValue", value: "1", unitCode: "MON" },
                  },
                  url: "https://antatlas.ai/pricing",
                },
                {
                  "@type": "Offer",
                  name: "Business",
                  price: "1490",
                  priceCurrency: "USD",
                  availability: "https://schema.org/InStock",
                  description:
                    "Series B+. 25-100 engineers, 25-100 repos, multi-team review with Champions, route-file detection, 400 drafts + 2,000 summaries per month.",
                  priceSpecification: {
                    "@type": "UnitPriceSpecification",
                    price: "1490",
                    priceCurrency: "USD",
                    referenceQuantity: { "@type": "QuantitativeValue", value: "1", unitCode: "MON" },
                  },
                  url: "https://antatlas.ai/pricing",
                },
                {
                  "@type": "Offer",
                  name: "Enterprise",
                  price: "4500",
                  priceCurrency: "USD",
                  availability: "https://schema.org/InStock",
                  description:
                    "100+ engineers, SOC 2 / SSO / SCIM required. Custom AI quota or BYOK ($2,900/mo). Dedicated AM, SLA, MSA, annual PO.",
                  priceSpecification: {
                    "@type": "UnitPriceSpecification",
                    price: "4500",
                    priceCurrency: "USD",
                    referenceQuantity: { "@type": "QuantitativeValue", value: "1", unitCode: "MON" },
                  },
                  url: "https://antatlas.ai/pricing",
                },
              ],
            },
          }),
        }}
        />
      </div>
    </section>
  );
};

export default Pricing;
